import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/features/utils/use-mobile";
import { cn } from "@/lib/utils";
import { CaretUpDownIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useState, createContext, useContext } from "react";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "@/features/utils/i18n";

// Context for sharing state between ResponsiveSelect components
interface ResponsiveSelectContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  readOnly: boolean;
}

const ResponsiveSelectContext =
  createContext<ResponsiveSelectContextType | null>(null);

const useResponsiveSelectContext = () => {
  const context = useContext(ResponsiveSelectContext);
  if (!context) {
    throw new Error(
      "ResponsiveSelect components must be used within ResponsiveSelect",
    );
  }
  return context;
};

// ResponsiveSelect components for Popover/Drawer pattern
export interface ResponsiveSelectProps {
  children: ReactNode;
  className?: string;
  readOnly?: boolean;
}

export const ResponsiveSelect = ({
  children,
  readOnly = false,
}: ResponsiveSelectProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const contextValue = { open, setOpen, readOnly };

  if (isMobile) {
    return (
      <ResponsiveSelectContext.Provider value={contextValue}>
        <Drawer open={open} onOpenChange={setOpen}>
          {children}
        </Drawer>
      </ResponsiveSelectContext.Provider>
    );
  }

  return (
    <ResponsiveSelectContext.Provider value={contextValue}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </ResponsiveSelectContext.Provider>
  );
};

export const ResponsiveSelectTrigger = ({
  children,
  placeholder,
  disabled = false,
  className,
  tooltip,
  icon,
}: {
  children: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  tooltip?: string;
  icon?: ReactNode;
}) => {
  const { t } = useTranslation();
  placeholder ??= t("common.select.placeholder");
  const { open, readOnly } = useResponsiveSelectContext();
  const isMobile = useIsMobile();

  // Disable interactions when readOnly
  const isDisabled = disabled || readOnly;

  if (isMobile) {
    return (
      <DrawerTrigger className={"w-full"} disabled={isDisabled}>
        <ResponsiveSelectButton
          open={open}
          disabled={isDisabled}
          className={className}
          placeholder={placeholder}
          tooltip={tooltip}
          icon={icon}
        >
          {children}
        </ResponsiveSelectButton>
      </DrawerTrigger>
    );
  }

  return (
    <PopoverTrigger className={"w-full"} disabled={isDisabled}>
      <ResponsiveSelectButton
        open={open}
        disabled={isDisabled}
        className={className}
        placeholder={placeholder}
        tooltip={tooltip}
        icon={icon}
      >
        {children}
      </ResponsiveSelectButton>
    </PopoverTrigger>
  );
};

export const ResponsiveSelectContent = ({
  children,
  className,
  title,
  align = "start",
  enableSearch = false,
  searchPlaceholder,
  emptyMessage,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  align?: "start" | "center" | "end";
  enableSearch?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
}) => {
  const { t } = useTranslation();
  searchPlaceholder ??= t("common.select.search");
  emptyMessage ??= t("common.select.noResults");
  const isMobile = useIsMobile();

  // Auto-enable search if there are more than 5 items
  const childCount = Array.isArray(children) ? children.length : 1;
  const shouldEnableSearch = enableSearch || childCount > 5;

  const content = (
    <Command>
      {shouldEnableSearch && (
        <CommandInput placeholder={searchPlaceholder} className="h-9" />
      )}
      <CommandList>
        <CommandEmpty>{emptyMessage}</CommandEmpty>
        <CommandGroup>{children}</CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <DrawerContent className={twMerge(className, "w-full max-h-dvh")}>
        {title && (
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
        )}
        <div className={cn("", !title && "pt-4")}>{content}</div>
      </DrawerContent>
    );
  }

  return (
    <PopoverContent
      className={cn(
        "w-[var(--radix-popover-trigger-width)] p-0 max-h-[var(--radix-popover-content-available-height)] overflow-auto",
        className,
      )}
      align={align}
    >
      {content}
    </PopoverContent>
  );
};

export interface ResponsiveSelectButtonProps {
  children: ReactNode;
  open?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  tooltip?: string;
  icon?: ReactNode;
}

const ResponsiveSelectButton = ({
  children,
  open,
  placeholder,
  disabled = false,
  className,
  tooltip,
  icon,
}: ResponsiveSelectButtonProps) => {
  const { t } = useTranslation();
  placeholder ??= t("common.select.placeholder");
  return (
    <Button
      variant="outline"
      type="button"
      aria-expanded={open}
      className={cn("w-full justify-between", className)}
      disabled={disabled}
      tooltip={tooltip}
    >
      <div className="flex items-center truncate w-full">
        {children || (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </div>
      {icon || <CaretUpDownIcon className="h-4 w-4 shrink-0 opacity-50" />}
    </Button>
  );
};

// ResponsiveSelectItem component
export interface ResponsiveSelectItemProps {
  children: ReactNode;
  value: string;
  onSelect?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  searchTerms?: string; // Additional search terms for better search functionality
  stayOpen?: boolean; // Whether to keep the select open after selection
}

export const ResponsiveSelectItem = ({
  children,
  value,
  onSelect,
  className,
  disabled = false,
  searchTerms,
  stayOpen = false,
}: ResponsiveSelectItemProps) => {
  const { setOpen, readOnly } = useResponsiveSelectContext();
  // Combine value with searchTerms for better search
  const searchValue = searchTerms ? `${value} ${searchTerms}` : value;

  const handleSelect = () => {
    // Don't allow selection when readOnly
    if (readOnly) return;

    onSelect?.(value);
    if (!stayOpen) setOpen(false); // Close the select after selection
  };

  return (
    <CommandItem
      value={searchValue}
      onSelect={handleSelect}
      className={className}
      disabled={disabled || readOnly}
    >
      {children}
    </CommandItem>
  );
};

// ResponsiveSelectValue component - displays the selected value or placeholder
export interface ResponsiveSelectValueProps {
  placeholder?: string;
  className?: string;
}

export const ResponsiveSelectValue = ({
  placeholder,
  className,
}: ResponsiveSelectValueProps) => {
  return (
    <span className={cn("text-muted-foreground", className)}>
      {placeholder}
    </span>
  );
};
